import 'mobx-react-lite/batchingForReactDom'

/// Import moment locales to make them available later
import 'moment/locale/da'

/// Load the React app module. It will take care of most bootstrapping
import { App } from '@morningtrain/react-app'
import { OperationRepository } from '@morningtrain/react-resources'
import { AuthService } from '@morningtrain/react-auth'
import Toastify from 'services/Toastify'
import { SmartviewManager } from '@morningtrain/react-smartviews'
import { LaravelTranslationService } from '@morningtrain/react-translations'
import DepartmentService from 'services/DepartmentService'
import ForcedNotificationsService from 'services/ForcedNotificationsService'
import { Spawner } from '@morningtrain/react-spawner'
import PageScaler from 'support/page/PageScaler'
import EventsManager from 'services/events/EventsManager'

import { registerLocale, setDefaultLocale } from 'react-datepicker'
import da from 'date-fns/locale/da'

registerLocale('da', da)
setDefaultLocale('da')

const app = App.create()

/// Optionally register additional plugins
/// A plugin is a class implementing a boot method
/// The boot method should accept an app instance as a parameter
/// In order to work with boot flow, the method should return a Promise
// app.register(SomePlugin);
console.log(app)

/// Define the services to wrap all pages in
/// It should be a series of react components that all render their children
app.services([
  EventsManager,
  OperationRepository,
  [AuthService, { reloadPageOnUnauthorized: true }],
  Toastify,
  LaravelTranslationService,
  SmartviewManager,
  DepartmentService,
  ForcedNotificationsService,
  Spawner
])

app.register(PageScaler)

/// Specify default page template
app.templates(require('templates'))
app.defaultTemplate('Admin')

/// Configure which React components to load into the application
/// Only components included in this object, can be added from Laravel Blade views.
/// It should primarily be the main app page components that are included here

app.setReactComponents('app', {
  Login: require('pages/shared/Login').default,
  AuthTablet: require('pages/app/AuthTablet').default,
  ForgotPassword: require('pages/shared/ForgotPassword').default,
  ResetPassword: require('pages/shared/ResetPassword').default,
  Dashboard: require('pages/app/Dashboard').default,
  SpecialDays: require('pages/app/SpecialDays').default,
  VacationRequests: {
    Index: require('pages/app/vacation/index').default,
    MyRequests: require('pages/app/vacation/MyRequests').default
  },
  Checklists: {
    Index: require('pages/app/checklists/index').default,
    Templates: require('pages/app/checklists/Templates').default,
    View: require('pages/app/checklists/View').default,
    Template: require('pages/app/checklists/CreateTemplate').default
  },
  Onboarding: {
    Overview: require('pages/app/onboarding/Overview').default,
    Checklist: require('pages/app/onboarding/Checklist').default,
    Templates: require('pages/app/onboarding/Templates').default,
    Template: require('pages/app/onboarding/Template').default,
    Employee: require('pages/app/onboarding/Employee').default
  },
  Cleaning: {
    Checkups: require('pages/app/cleaning/Checkups').default,
    Templates: {
      Index: require('pages/app/cleaning/Templates').default,
      Edit: require('pages/app/cleaning/EditTemplate').default
    },
    Summary: require('pages/app/cleaning/Summary').default,
    PerformCheckup: require('pages/app/cleaning/PerformCheckup').default
  },
  HR: {
    Template: require('pages/app/hr/template').default,
    Contracts: require('pages/app/hr/Contracts').default,
    MyContracts: require('pages/app/hr/MyContracts').default,
    ContractTemplates: require('pages/app/hr/ContractsTemplates').default,
    AMO: require('pages/app/hr/amo/index').default,
    ViewAMO: require('pages/app/hr/amo/view').default,
    Messages: require('pages/app/hr/messages').default,
    Salary: {
      Overview: require('pages/app/hr/salary/SalaryOverview').default,
      Personal: require('pages/app/hr/salary/SalaryPersonal').default
    },
    Badges: require('pages/app/hr/badges').default,
    Seniority: require('pages/app/hr/seniority').default,
    Employments: require('pages/app/hr/Employments').default
  },
  Economy: {
    Index: require('pages/app/economy/index').default,
    Revenue: require('pages/app/economy/revenue').default,
    AvgBasketSize: require('pages/app/economy/AvgBasketSize').default,
    GuestCount: require('pages/app/economy/GuestCount').default,
    Goodsusage: require('pages/app/economy/goodsusage').default,
    WageRatio: require('pages/app/economy/WageRatio').default,
    Discounts: require('pages/app/economy/discounts').default,
    OpenOrders: require('pages/app/economy/openOrders').default
  },
  Files: {
    EmployeeFiles: {
      ManageTag: require('pages/app/files/ManageEmployeeFileTag').default,
      Manage: require('pages/app/files/ManageEmployeeFiles').default,
      View: require('pages/app/files/ViewEmployeeFiles').default,
      ViewTag: require('pages/app/files/ViewEmployeeFilesTag').default
    }
  },
  EmailTemplates: {
    Index: require('pages/app/emailtemplates/EmailTemplatesIndexPage').default
  },
  Tablet: {
    Toggle: require('pages/app/tablet/Toggle').default
  },
  Shifts: {
    Planner: require('pages/app/shifts/Planner').default,
    View: require('pages/app/shifts/View').default,
    Entries: require('pages/app/shifts/Entries').default,
    EntriesManage: require('pages/app/shifts/EntriesManage').default,
    Exchange: {
      Overview: require('pages/app/shifts/exchange/ShiftExchangeOverview').default
    }
  },
  Inventory: {
    Overview: require('pages/app/inventory/Overview').default,
    Delivery: require('pages/app/inventory/Delivery').default,
    Norms: require('pages/app/inventory/Norms').default,
    Stocktake: require('pages/app/inventory/Stocktake').default,
    Item: {
      Index: require('pages/app/inventory/item/Index').default,
      Create: require('pages/app/inventory/item/Create').default
    },
    Supplier: {
      Index: require('pages/app/inventory/supplier/Index').default,
      Create: require('pages/app/inventory/supplier/Create').default,
      Departments: require('pages/app/inventory/supplier/Departments').default
    },
    Order: {
      Edit: require('pages/app/inventory/order/Edit').default
    }
  },
  Settings: {
    Departments: {
      Index: require('pages/app/settings/departments/DepartmentsIndex').default,
      Edit: require('pages/app/settings/departments/DepartmentsEdit').default
    },
    DepartmentSuppliers: {
      Index: require('pages/app/settings/department_suppliers/DepartmentsIndex').default,
      Edit: require('pages/app/settings/department_suppliers/DepartmentsEdit').default
    },
    Permissions: require('pages/app/settings/permissions/Permissions').default,
    EmploymentTypes: {
      Index: require('pages/app/settings/employment_types/EmploymentTypesIndex').default,
      Edit: require('pages/app/settings/employment_types/EmploymentTypesEdit').default,
      Create: require('pages/app/settings/employment_types/EmploymentTypesCreate').default
    },
    Badges: require('pages/app/settings/Badges').default,
    Inventory: {
      Index: require('pages/app/settings/inventory/InventoryIndex').default,
      Edit: require('pages/app/settings/inventory/InventoryEdit').default,
    },
  },
  Users: {
    Index: require('pages/app/users/users/Index').default,
    Edit: require('pages/app/users/users/Edit').default,
    Create: require('pages/app/users/users/Create').default,
    Profile: require('pages/app/users/Profile').default
  },
  Coworkers: {
    Overview: require('pages/app/coworkers/Overview').default
  },
  Suppliers: {
    Overview: require('pages/app/suppliers/Overview').default
  }
})

/// Boot the application
/// It will loop through all applied plugins and load them in order.
/// After booting all plugins, it will listen to a document ready event
/// When the DOM is ready, it will then mount and render all react components present in the DOM
app.run()
